'use client'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { motion } from 'framer-motion'

import type { Page } from '@/payload-types'
import { HeroBottomMask } from '@/components/HeroBottomMask'
import { formatImageUrl } from '@/utilities/formatImageUrl'
import { getPreferredMediaSource } from '@/utilities/getPreferredMediaSource'

import {
  Carousel,
  CarouselContent,
  CarouselNavigation,
  CarouselIndicator,
  CarouselProgress,
  CarouselItem,
  useCarousel,
} from "./Carousel"
import SlideContent from './CarouselContent'
import { cn } from '@/utilities/ui'

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
)

function CarouselThumbnails({ slides }: { slides: NonNullable<Page['hero']['slides']> }) {
  const { index, setIndex } = useCarousel()
  
  if (!slides || slides.length === 0) return null

  // Calculate translation so we never show empty space if we reach the end
  const maxIndex = Math.max(0, slides.length - 3)
  const translateIndex = Math.min(index, maxIndex)

  return (
    <div className="absolute bottom-6 left-0 w-full z-20 hidden lg:block">
      <div className="container mx-auto px-6 relative h-full">
        <div className="overflow-hidden w-full relative bg-transparent">
          <motion.div 
            className="flex w-full"
          animate={{ x: `-${translateIndex * 33.333333}%` }}
          transition={{ type: "spring", stiffness: 90, damping: 18, duration: 0.3 }}
        >
          {slides.map((slide, i) => {
             const image = slide.image;
             if (typeof image !== 'object' || image === null || !image.url) return null;
             const imgSrc = formatImageUrl(image.url)
             const isActive = i === index
             
             return (
               <div 
                 key={slide.id || i}
                 onClick={() => setIndex(i)}
                 className="w-[33.333333%] shrink-0 px-4 cursor-pointer"
               >
                 <div className={cn(
                    "flex h-[90px] w-full overflow-hidden group/thumb transition-all relative",
                    isActive ? "border-t border-transparent bg-transparent" : "border-t border-white/20 hover:border-white/50 bg-transparent"
                  )}>
                   {/* Active Progress Bar Indicator (Top) */}
                   {isActive && (
                     <motion.div 
                       layoutId="active-thumb-bar" 
                       className="absolute top-0 left-0 w-full h-[1px] bg-primary z-10 mb-4" 
                       initial={{ opacity: 0 }}
                       animate={{ opacity: 1 }}
                       transition={{ duration: 0.2 }}
                     />
                   )}

                   <div className="w-[90px] h-full relative shrink-0 mt-6">
                     <Image src={imgSrc} alt={image.alt || ''} fill className="object-cover" sizes="270px" quality={90} />
                   </div>
                   <div className="p-4 flex-1 flex flex-col justify-center">
                     <h4 className="text-white text-[13px] font-semibold line-clamp-2 leading-tight mb-1.5 group-hover/thumb:text-gray-300 transition-colors">{slide.title}</h4>
                     <span className="text-gray-400 text-[10px] flex items-center gap-1.5 font-medium uppercase tracking-wider">
                       <ClockIcon className="w-3 h-3 text-accent"/> {slide.cta2Text || 'Book Now'}
                     </span>
                   </div>
                 </div>
               </div>
             )
          })}
        </motion.div>
        </div>
      </div>
    </div>
  )
}

export const HighImpactHero: React.FC<Page['hero']> = ({ slides }) => {
  const { setHeaderTheme } = useHeaderTheme()
  const [mounted, setMounted] = useState(false)

  const firstSlide = slides?.[0]
  const firstSlideImage =
    firstSlide && typeof firstSlide.image === 'object' && firstSlide.image !== null
      ? firstSlide.image
      : null
  const firstSlideSrc = firstSlideImage?.url
    ? formatImageUrl(getPreferredMediaSource(firstSlideImage, ['xlarge', 'large']).url)
    : null

  useEffect(() => {
    setHeaderTheme('dark')
    setMounted(true)
  }, [setHeaderTheme])

  if (!mounted && firstSlideSrc) {
    return (
      <div className="relative w-full h-[700px] lg:h-[800px] group">
        <div className="relative w-full h-full overflow-hidden">
          <Image
            src={firstSlideSrc}
            alt={firstSlideImage?.alt || ''}
            fill
            className="object-cover"
            sizes="100vw"
            priority
            fetchPriority="high"
            decoding="async"
            quality={100}
          />
          <div className="absolute inset-0 bg-black/20" />
          <div className="container mx-auto px-4 flex items-end justify-center z-99 relative -bottom-78">
            <div className="container mx-auto relative text-white">
              <div className="text-accent text-xl md:text-2xl font-oswald uppercase tracking-wider mb-4 p-3 w-fit bg-gray-50/20 rounded-xs">
                {firstSlide?.title}
              </div>
              <p className="text-sm md:text-xl opacity-90">{firstSlide?.subtitle || ''}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-[700px] lg:h-[800px] group">
      <Carousel autoPlay autoPlayInterval={7000}>
        <CarouselContent>
          {slides?.map((slide, index) => {
            const { image, title, subtitle } = slide;
            if (typeof image === 'object' && image !== null && image.url) {
              return (
                <CarouselItem key={slide.id || index} className="relative">
                  <SlideContent
                    image={{
                      src: formatImageUrl(getPreferredMediaSource(image, ['xlarge', 'large']).url),
                      alt: image.alt || '',
                      title: title,
                      subtitle: subtitle || '',
                      offerTitle: slide.offerTitle || '',
                      offerDescription: slide.offerDescription || '',
                      offerBtnText: slide.offerBtnText || '',
                      offerUrl: slide.offerUrl || '',
                      cta1Text: slide.cta1Text || '',
                      cta2Text: slide.cta2Text || '',
                    }}
                    index={index}
                  />
                </CarouselItem>
              )
            }
            return null;
          })}
        </CarouselContent>
        <CarouselNavigation alwaysShow />
        <CarouselIndicator />
        <CarouselProgress />
        <CarouselThumbnails slides={slides!} />
      </Carousel>
    </div>
  )
}
